import type {
  BuyerDealStructureStatus,
  DealStructureCashFlowType,
  DealStructureDownPaymentSource,
  DealStructurePriceBasis,
} from "@/api/buyers";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";

/**
 * Every staff-facing word of the Potential Deal Structure surfaces: the Deal
 * structure tab, BUILD THE DEAL STRUCTURE (W0–W5) and the EDIT modal.
 *
 * <p>🔴 <b>Owner-approved copy, verbatim</b> (brief Appendix B, as amended by
 * ruling Q1, and Appendix C). The structure is Phoenix's illustration — never
 * the buyer's statement, and nothing here asks staff to contact the buyer. The
 * word "offer" appears only in the negation "It is not an offer from the
 * buyer".</p>
 *
 * <p>No figure is computed here: every number the surface shows is read from
 * the server's `BuyerDealStructureDto`. These helpers only format.</p>
 */

export const BUILD_BUTTON = "BUILD THE DEAL STRUCTURE";
export const EDIT_BUTTON = "EDIT DEAL STRUCTURE";
export const SAVE_BUTTON = "SAVE DEAL STRUCTURE";

export const STATUS_LABELS: Record<BuyerDealStructureStatus, string> = {
  NotBuilt: "Not built",
  InProgress: "In progress",
  Complete: "Complete",
};

// ---------------------------------------------------------------- Appendix C

export const STRUCTURE_CARD = {
  title: "Deal structure",
  emptyTitle: "Not built yet",
  emptyBody: "Use BUILD THE DEAL STRUCTURE to prepare it from the listing and the buyer's prequalification.",
  rows: {
    price: "Price",
    priceBasis: "Price basis",
    downPayment: "Down payment",
    // D7 — the 1 November 2026 structure: the headline is the 20-year schedule,
    // and the 10-year schedule shows beside it.
    financed: "Amount financed · 20-year",
    financedTenYear: "Amount financed · 10-year",
    financedMultiple: "Financed multiple · 20-year",
    balance: "Balance to be resolved · 20-year",
    balanceTenYear: "Balance to be resolved · 10-year",
    note: "Phoenix note",
  },
} as const;

/** "Annual cash flow ({type})" — for SDE the row shows the free cash flow
 *  estimate and reads "SDE, estimated". */
export function annualCashFlowLabel(type: DealStructureCashFlowType | null | undefined): string {
  if (type === "Sde") return "Annual cash flow (SDE, estimated)";
  return type ? `Annual cash flow (${CASH_FLOW_TYPE_LABELS[type]})` : "Annual cash flow";
}

export const SUGGESTED_CARD = {
  title: "Suggested structure",
  body: "From the listing and the buyer's prequalification. Use BUILD THE DEAL STRUCTURE to confirm each figure.",
  askingPrice: "Asking price",
  supported: "Supported by cash flow",
  askingFromSubmission: "asking at submission",
  askingFromListing: "from the listing",
  cashFlowFromListing: "from the listing",
  supportedSource: "the down payment plus the 20-year Maximum Financed Amount",
  supportedCapped: "financing capped at the program's $10 million maximum",
  unavailable: {
    typeUnknown: "Choose the cash-flow type in BUILD THE DEAL STRUCTURE",
    sde: "Needs a free cash flow estimate",
    noCashFlow: "The listing has no cash flow figure",
    noDownPayment: "No down payment from the buyer's prequalification or assessment",
  },
} as const;

/** "Cash flow ({type})", or "Cash flow (type not recorded)" (ruling Q2). */
export function suggestedCashFlowLabel(type: DealStructureCashFlowType | null | undefined): string {
  return type ? `Cash flow (${CASH_FLOW_TYPE_LABELS[type]})` : "Cash flow (type not recorded)";
}

/** "Down payment ({source used})". */
export function suggestedDownPaymentLabel(source: DealStructureDownPaymentSource | null | undefined): string {
  return source ? `Down payment (${DOWN_PAYMENT_SOURCE_WORDS[source]})` : "Down payment";
}

export const EDIT_MODAL = {
  title: "Edit the deal structure",
  notice:
    "Saving confirms this structure was prepared by Phoenix from the listing and the buyer's "
    + "prequalification and assessment. It is not an offer from the buyer.",
} as const;

// ---------------------------------------------------------------- Appendix B

export const WIZARD_TITLE = "Build the deal structure";

export const W0 = {
  title: "Build the deal structure",
  body:
    "You're preparing a Potential Deal Structure for the listing broker and the seller. It is "
    + "Phoenix's illustration of how our program could finance this listing for this buyer. It is "
    + "not an offer from the buyer, and the buyer is not contacted for it. Each step is prefilled "
    + "from the listing and the buyer's prequalification. Check each figure and change it where "
    + "you know better.",
  start: "Start",
  notNow: "Not now",
} as const;

export const W1 = {
  question: "Which annual cash flow should the structure use?",
  amountLabel: "Annual cash flow",
  sdeEstimateLabel:
    "Estimated free cash flow after paying a full-time manager and after annual maintenance capital "
    + "expenditure",
  sdeNote:
    "SDE includes the owner's own pay. The program's 5× limit is applied to free cash flow, so "
    + "estimate what the business would earn after paying someone to run it and after annual "
    + "maintenance capital expenditure. The document will say this figure was estimated by Phoenix.",
  amountAboveZero: "The cash flow must be above zero.",
  chooseType: "Choose the cash-flow type.",
} as const;

/** "The listing reports {amount} ({type} / type not recorded)." */
export function listingCashFlowLine(
  amount: number | null | undefined,
  type: DealStructureCashFlowType | null | undefined,
): string | null {
  if (amount == null) return null;
  const kind = type ? CASH_FLOW_TYPE_LABELS[type] : "type not recorded";
  return `From the listing: ${formatCurrency(amount)} (${kind}).`;
}

export const W2 = {
  question: "What down payment should the structure show?",
  amountLabel: "Down payment",
  staffNote:
    "This is our estimate from what the buyer told us. The document says so, and says the final "
    + "figure is negotiated between the buyer and the seller. You don't need to call the buyer to "
    + "confirm it.",
  zeroWarning: "A zero down payment is allowed, but check that it's what you intend.",
} as const;

/** "{amount}, from the buyer's {prequalification / assessment} ({date})". */
export function downPaymentPrefillLine(
  amount: number,
  source: DealStructureDownPaymentSource | null | undefined,
  date: string | null,
): string {
  const from = source && source !== "Staff" ? `, from the buyer's ${DOWN_PAYMENT_SOURCE_WORDS[source]}` : "";
  return `${formatCurrency(amount)}${from}${date ? ` (${date})` : ""}`;
}

export const W3 = {
  question: "Which price should the structure use?",
  asking: "Asking price",
  supported: "Supported by cash flow",
  supportedTail:
    " — the down payment plus the 20-year Maximum Financed Amount (up to 5× free cash flow, no more "
    + "than $10 million financed)",
  custom: "Another price",
  customPlaceholder: "$______",
  customLabel: "Another price",
  belowDownPayment: "The price can't be lower than the down payment.",
  noAsking: "This listing has no asking price. Choose another price basis.",
} as const;

export const W4 = {
  heading: "Review",
  noteLabel: "Phoenix note (optional, prints on the document, 300 characters max)",
  amountFinanced: "Amount financed",
  amountFinancedCaption: "of price · 20-year schedule",
  scheduleHeads: { twentyYear: "20-year", tenYear: "10-year" },
  rows: {
    maximum: "Maximum Financed Amount",
    financed: "Amount financed",
    balance: "Balance to be resolved",
    financedMultiple: "Financed multiple",
    coverage: "Coverage of scheduled principal and interest",
    originationCoverage: "Origination Coverage Ratio (staff only)",
    sellerHeading: "What the seller could receive",
  },
  /** C11 — the seller panel points to the seller's documents; no receipts figures (D7). */
  sellerPointer:
    "The seller's costs, receipts and pool options are set out in the Phoenix seller program brochure "
    + "and the seller's program documents.",
} as const;

export const W5 = {
  confirm:
    "I prepared this structure from the listing and the buyer's prequalification and assessment. "
    + "It is not an offer from the buyer.",
  save: SAVE_BUTTON,
  goBack: "Go back to a step",
  saved: "Deal structure saved.",
} as const;

/** The six steps, in order; the index is what `step` stores. */
export const WIZARD_STEPS = [
  { key: "opening", label: "Start" },
  { key: "cashFlow", label: "Cash flow" },
  { key: "downPayment", label: "Down payment" },
  { key: "price", label: "Price" },
  { key: "review", label: "Review" },
  { key: "confirm", label: "Confirm" },
] as const;

export const CONFIRM_STEP_INDEX = WIZARD_STEPS.length - 1;

/** The Phoenix note's limit (D15) — `DealStructureCalculator.MaxNoteLength`. */
export const NOTE_MAX_LENGTH = 300;

// ---------------------------------------------------------------- labels

export const CASH_FLOW_TYPE_LABELS: Record<DealStructureCashFlowType, string> = {
  FreeCashFlow: "Free cash flow",
  Ebitda: "EBITDA",
  Sde: "SDE",
};

export const CASH_FLOW_TYPES: readonly DealStructureCashFlowType[] = ["FreeCashFlow", "Ebitda", "Sde"];

export const PRICE_BASIS_LABELS: Record<DealStructurePriceBasis, string> = {
  Asking: W3.asking,
  Supported: W3.supported,
  Custom: W3.custom,
};

const DOWN_PAYMENT_SOURCE_WORDS: Record<DealStructureDownPaymentSource, string> = {
  Prequalification: "prequalification",
  Assessment: "assessment",
  Staff: "set by Phoenix",
};

// ---------------------------------------------------------------- formatting

/** A 0–1 fraction as a percent: 0.2 → "20%", 0.075 → "7.5%". */
export function percentOf(fraction: number | null | undefined): string {
  if (fraction == null) return "";
  return formatNumber(fraction, { style: "percent", maximumFractionDigits: 1 });
}

/** A multiple: 4.5 → "4.5x". */
export function multipleOf(value: number | null | undefined): string {
  if (value == null) return "";
  return `${formatNumber(value, { maximumFractionDigits: 2 })}x`;
}

/**
 * A typed money figure, or null when the text is not one. `allowZero` is for
 * the down payment, where $0 is a real answer (with a warning).
 */
export function parseMoneyText(text: string, allowZero = false): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n > 0 || (allowZero && n === 0) ? n : null;
}

/** The text a money field starts from. */
export function moneyText(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}
